import React from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Clock, ShieldCheck, ClipboardCheck, AlertTriangle, RefreshCw, BookOpen } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";

const GCSB_CPD = "https://gcsb.uk/cpd";
const HOURS = [["CSCE", 10, "Foundation"], ["CSP", 15, "Professional"], ["CSL", 20, "Leadership"], ["CCSO", 25, "Executive"], ["PGDCSL", 25, "Postgraduate"]];
const ACTIVITIES = ["Structured learning", "Conference / seminar", "Publication / research", "Professional contribution", "Self-directed study"];
const WHY = [
  { icon: ShieldCheck, title: "Keeps your designation active", text: "Under CSI-REG-PGDCSL-007, a CSI designation stays Active only while the annual CPD requirement is met and declared to the Board." },
  { icon: RefreshCw, title: "Keeps pace with the standards", text: "Converged security, CSF-STRAT and the GCSB standards (currently Version 2.0, issued and effective 13 October 2026 - gcsb.uk/standards) evolve. CPD makes sure holders apply current practice, not the practice of the year they qualified." },
  { icon: ClipboardCheck, title: "Assures employers and the public", text: "CPD is part of the CSI Quality Assurance Framework: the public register shows current status, so employers can rely on the credential being maintained." },
  { icon: BookOpen, title: "Sustains fellowship standing", text: "Members and Fellows maintain standing and professional conduct through continuous learning, contribution and reflection." },
];

export default function CPDRenewalPage() {
  useSeo({
    title: "CPD & Renewal",
    description: "Annual Continuing Professional Development requirements for CSI credentials: CSCE 10h, CSP 15h, CSL 20h, CCSO 25h, PGDCSL 25h - declared to GCSB and subject to audit.",
    path: "/cpd-renewal",
  });
  return (
    <div data-testid="cpd-renewal-page">
      <section className="bg-[var(--navy-deep)] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="eyebrow !text-[var(--gold-soft)]">CPD &amp; Renewal</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight mt-2 max-w-4xl">Annual Continuing Professional Development</h1>
          <p className="text-zinc-300 mt-4 max-w-3xl text-sm sm:text-base">
            Earning a CSI credential is the beginning, not the end. Active credential status is maintained through annual CPD,
            declared to the Global Converged Security Board (GCSB) and subject to audit - as required by the CSI Quality Framework and
            the CPD, Maintenance of Designation and Fellowship Standing Regulations (CSI-REG-PGDCSL-007).
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href={GCSB_CPD} target="_blank" rel="noopener noreferrer" className="btn-terracotta px-5 py-3 rounded-full text-sm font-semibold inline-flex items-center gap-2" data-testid="gcsb-cpd-link">
              Submit CPD at GCSB <ExternalLink size={13}/>
            </a>
            <Link to="/cpd" className="chip px-5 py-3 bg-white/5 border-white/20 text-white hover:bg-white/10" data-testid="cpd-tracker-link">My CPD tracker</Link>
            <Link to="/policies#cpd" className="chip px-5 py-3 bg-white/5 border-white/20 text-white hover:bg-white/10" data-testid="cpd-policy-link">Read the CPD regulations</Link>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="eyebrow mb-1">Annual CPD framework</div>
        <h2 className="font-display text-3xl sm:text-4xl mb-6">Hours required per year</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {HOURS.map(([code, h, level]) => (
            <div key={code} className="card-warm p-5" data-testid={`cpd-hours-${code}`}>
              <div className="text-xs font-bold tracking-widest text-[var(--gold-deep)]">{code}</div>
              <div className="font-display text-4xl mt-1">{h}<span className="text-base text-zinc-500 ml-1">hours</span></div>
              <div className="text-xs text-zinc-500 mt-1 inline-flex items-center gap-1"><Clock size={11}/> {level} · per year</div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="eyebrow mb-1">Why CPD is required</div>
        <h2 className="font-display text-3xl sm:text-4xl mb-6">Part of our quality framework</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {WHY.map(({ icon: Icon, title, text }) => (
            <div key={title} className="card-warm p-5 flex gap-4">
              <div className="w-10 h-10 rounded-xl bg-[var(--navy-deep)] text-[var(--gold-soft)] grid place-items-center shrink-0"><Icon size={18}/></div>
              <div><h3 className="font-display text-xl">{title}</h3><p className="text-sm text-zinc-700 mt-1">{text}</p></div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 grid lg:grid-cols-2 gap-4">
        <div className="card-warm p-6">
          <h3 className="font-display text-2xl">Eligible activity types</h3>
          <ul className="mt-3 space-y-2 text-sm text-zinc-700">
            {ACTIVITIES.map((a) => <li key={a} className="flex gap-2"><ClipboardCheck size={15} className="text-[var(--gold-deep)] mt-0.5 shrink-0"/>{a}</li>)}
          </ul>
        </div>
        <div className="card-warm p-6">
          <h3 className="font-display text-2xl">How to stay compliant</h3>
          <ol className="mt-3 space-y-2 text-sm text-zinc-700 list-decimal pl-5">
            <li>Log your activity through the year in your CSI CPD tracker.</li>
            <li>Declare it annually to GCSB through the CPD submission portal with your credential ID.</li>
            <li>Keep evidence (certificates, agendas, outputs) - declarations are subject to audit.</li>
          </ol>
          <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 flex gap-2" data-testid="cpd-lapse-note">
            <AlertTriangle size={15} className="shrink-0 mt-0.5"/>
            If the annual requirement is not met and declared, the credential may no longer show as Active in the public register until CPD is brought up to date.
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="card-warm p-6 flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm sm:text-base text-zinc-700 max-w-2xl">Ready to declare your hours? Submit your CPD record directly to the Board.</p>
          <a href={GCSB_CPD} target="_blank" rel="noopener noreferrer" className="btn-ink px-5 py-3 rounded-full text-sm font-semibold inline-flex items-center gap-2" data-testid="gcsb-cpd-link-bottom">
            Open gcsb.uk/cpd <ExternalLink size={13}/>
          </a>
        </div>
      </section>
    </div>
  );
}
